interface DebouncedFunction<T extends (...args: never[]) => void> {
  (...args: Parameters<T>): void;
  cancel: () => void;
}

/**
 * Creates a debounced version of a function that delays its execution until after a specified wait time has elapsed since the last call.
 * T - Type of the function to debounce.
 * @param func - The function to debounce.
 * @param wait - Delay in milliseconds before invoking the function after the last call.
 * @returns A debounced function that postpones execution until the wait time has passed.
 */
export function debounce<T extends (...args: never[]) => void>(
  func: T,
  wait: number,
): DebouncedFunction<T> {
  let timeout: ReturnType<typeof setTimeout> | null;

  const debounced = function (...args: Parameters<T>): void {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };

  debounced.cancel = () => {
    if (timeout) clearTimeout(timeout);
    timeout = null;
  };

  return debounced;
}
